import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react';
import { saveTask } from '../../reducers/taskReducer'
import { updateTaskService } from '../../utils/taskService'
import TaskFields from './TaskFields'

export default function TaskSetup({ task: initialTask, onStart }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { getToken, userId } = useAuth();

  const [pendingTask, setPendingTask] = useState(initialTask)
  const [isNotificationVisible, setIsNotificationVisible] = useState(false)
  const [isShowEditState, setIsShowEditState] = useState(false)
  const taskProject = useSelector(state => 
    state.projectList?.projects?.find(project => 
      project.id === pendingTask?.metaData?.projectId
    )
  )

  useEffect(() => {
    setPendingTask(initialTask)
    setIsShowEditState(false)
  }, [initialTask])

  const reOpenTask = async () => {
    const updatedTask = { ...pendingTask, completed: false, archived: false }
    await updateTaskService(getToken, userId, updatedTask)
    dispatch(saveTask({ task: updatedTask }))
    setPendingTask(updatedTask)
    showNotification()
  }

  const handleScheduleForLater = async () => {
    let updatedTask = {}
    if (pendingTask?.startBy || pendingTask?.endBy) {
      updatedTask = { ...pendingTask, status: 'ready' }
    } else {
      updatedTask = { ...pendingTask, status: 'pending' }
    }
    await updateTaskService(getToken, userId, updatedTask)
    dispatch(saveTask({ task: updatedTask }))
    setIsShowEditState(false)
    showNotification()
  }

  const handleTaskUpdate = (updatedTask) => {
    setPendingTask(updatedTask)
  }

  const handleStartNow = async () => {    
    const updatedTask = await updateTaskService(getToken, userId, { ...pendingTask, status: 'ready' })
    await new Promise(resolve => {
      dispatch(saveTask({ task: updatedTask }));
      // Give Redux a chance to update the state
      setTimeout(resolve, 100);
    });
    onStart();
  }

  const showReadyState = () => {
    return pendingTask?.status === 'ready' 
      && !isShowEditState
      && (!taskProject || taskProject?.step === 'in_progress');
    }

  const showNotification = () => {
    setIsNotificationVisible(true)
    setTimeout(() => {
      setIsNotificationVisible(false)
    }, 2500)
  }

  const getTaskActions = () => {
    if (taskProject?.id && taskProject?.step !== 'in_progress') {
      return (
        <div className={`flex justify-end gap-3 pt-6 ${pendingTask?.completed || pendingTask?.archived ? 'opacity-50 pointer-events-none' : ''}`}>
          <button
            onClick={() => navigate(-1)}
            className="flex px-4 py-2 border border-gray-200 rounded-lg text-gray-600 bg-gray-200 hover:bg-gray-300 transition-colors"
            data-amp-track-section="Task Setup"
            data-amp-track-action="Cancel_Edit_Click"
            data-amp-track-description="Cancel Edit"
          >
            Cancel Edit
          </button>
          <button
            onClick={() => handleScheduleForLater()}
            className="flex px-4 py-2 text-white rounded-lg bg-stone-700 hover:bg-stone-800 transition-colors"
            data-amp-track-section="Task Setup"
            data-amp-track-action="Save_Exit_Click"
            data-amp-track-description="Save and Exit"
          >
            Save and Exit
          </button>
        </div>   
      );
    }

    return (
      <div className={`flex justify-end gap-3 pt-6 ${pendingTask?.completed || pendingTask?.archived ? 'opacity-50 pointer-events-none' : ''}`}>
        <button
          onClick={() => handleScheduleForLater()}
          className="flex px-4 py-2 border border-gray-200 rounded-lg text-gray-600 bg-gray-200 hover:bg-gray-300 transition-colors"
          data-amp-track-section="Task Setup"
          data-amp-track-action="Save_Click"
          data-amp-track-description="Save Task"
        >
          Save
        </button>
        <button
          onClick={() => handleStartNow()}
          className="flex px-4 py-2 text-white rounded-lg bg-sky-500 hover:bg-sky-600 transition-colors"
          data-amp-track-section="Task Setup"
          data-amp-track-action="Start_Now_Click"
          data-amp-track-description="Start Now"
        >
          Guide Me
        </button>
      </div>   
    )
  }

  return (
    <div className="flex flex-grow flex-col w-full p-5 rounded-lg border-t-2 border-slate-200 overflow-y-auto">
      <div className="space-y-6">
        <div>
          <p className='text-sm'>Greetings! It is a great pleasure to help you complete this task.</p>
          {pendingTask?.completed || pendingTask?.archived ? (
            <div className="space-y-4 border-b border-gray-200 pb-4">
              <p className='text-sm'>Alas, this task indicates that it has been completed or archived. However, you can re-open it if you need to.</p>
              <button
                onClick={() => reOpenTask()}
                className="flex px-4 py-2 ml-0 items-center gap-2 rounded-lg text-white bg-stone-700 hover:bg-stone-800 transition-colors"
                data-amp-track-section="Task Setup"
                data-amp-track-action="Reopen_Task_Click"
                data-amp-track-description="Re-open Task"
              >
                Re-open Task
              </button>
            </div>
          ) : (
            <p className='text-sm'>Alas, we have not yet started this task.</p>
          )}
        </div>

      {(showReadyState() && (!pendingTask?.completed && !pendingTask?.archived)) ? (
          <div>
            <p className='text-sm'>This task appears ready to be started.</p>
            <div className="flex justify-start gap-3 pt-6">
              <button
                onClick={() => setIsShowEditState(true)}
                className="flex px-4 py-2 ml-0 border border-gray-200 rounded-lg text-gray-600 bg-gray-200 hover:bg-gray-300 transition-colors"
                data-amp-track-section="Task Setup"
                data-amp-track-action="Edit_Task_Click"
                data-amp-track-description="Edit Task"
              >
                Edit Task
              </button>
              <button
                onClick={() => handleStartNow()}
                className="flex px-4 py-2 text-white rounded-lg bg-sky-500 hover:bg-sky-600 transition-colors"
                data-amp-track-section="Task Setup"
                data-amp-track-action="Start_Now_Click"
                data-amp-track-description="Start Now"
              >
                Guide Me
              </button>
            </div>
          </div>
        ) : (
          <>
            <TaskFields 
              task={pendingTask} 
              onUpdateTask={handleTaskUpdate} 
              ampSection="Task Setup"
            />
            {getTaskActions()}         
          </>
        )}
      </div>

      {isNotificationVisible && (
        <div className="fixed top-4 right-4 bg-green-500 text-lg text-bold text-white p-6 rounded-md shadow-lg z-50">
          Task updated successfully!
        </div>
      )}
    </div>
  )
}
